const QUEUE_KEY = 'nautique_offline_log_queue';
const PROBE_ENDPOINT = '/ik-endpoint';
const DEFAULT_PROBE_INTERVAL_MS = 45000;

let monitorIntervalId = null;
let lastProbeReachable = null;
let probeInFlight = false;
let probeListeners = new Set();

function readQueue() {
    try {
        const raw = localStorage.getItem(QUEUE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch (error) {
        return [];
    }
}

function writeQueue(queue) {
    try {
        localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
        // Ignore storage failures (private mode, quota, etc.)
    }
}

function enqueue(payload) {
    const queue = readQueue();
    queue.push({
        ...payload,
        queuedAt: new Date().toISOString(),
    });
    writeQueue(queue);
}

function notifyProbeListeners(reachable) {
    probeListeners.forEach((listener) => {
        try {
            listener(reachable);
        } catch (error) {
            // Ignore listener failures
        }
    });
}

export function onConnectivityProbeChange(listener) {
    probeListeners.add(listener);

    return () => {
        probeListeners.delete(listener);
    };
}

export function getGeolocation() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve({
                available: false,
                reason: 'unsupported',
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    available: true,
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    altitude: position.coords.altitude,
                    altitudeAccuracy: position.coords.altitudeAccuracy,
                    heading: position.coords.heading,
                    speed: position.coords.speed,
                    timestamp: position.timestamp,
                });
            },
            (error) => {
                resolve({
                    available: false,
                    code: error.code,
                    reason: error.message,
                });
            },
            {
                enableHighAccuracy: false,
                timeout: 8000,
                maximumAge: 120000,
            }
        );
    });
}

export async function collectDeviceInfo(extra = {}) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    let battery = null;

    if (navigator.getBattery) {
        try {
            const batteryStatus = await navigator.getBattery();
            battery = {
                charging: batteryStatus.charging,
                level: batteryStatus.level,
                chargingTime: batteryStatus.chargingTime,
                dischargingTime: batteryStatus.dischargingTime,
            };
        } catch (error) {
            battery = {
                error: error?.message || 'unavailable',
            };
        }
    }

    const geolocation = await getGeolocation();

    return {
        collectedAt: new Date().toISOString(),
        navigator: {
            onLine: navigator.onLine,
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            languages: navigator.languages,
            cookieEnabled: navigator.cookieEnabled,
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory,
            maxTouchPoints: navigator.maxTouchPoints,
            vendor: navigator.vendor,
        },
        screen: {
            width: screen.width,
            height: screen.height,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth,
            orientation: screen.orientation?.type,
        },
        window: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,
            locationHref: window.location.href,
            referrer: document.referrer,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        connection: connection
            ? {
                effectiveType: connection.effectiveType,
                downlink: connection.downlink,
                rtt: connection.rtt,
                saveData: connection.saveData,
                type: connection.type,
            }
            : null,
        battery,
        geolocation,
        ...extra,
    };
}

export async function probeNetworkReachability() {
    const startedAt = performance.now();

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return {
            reachable: false,
            endpoint: PROBE_ENDPOINT,
            latencyMs: Math.round(performance.now() - startedAt),
            reason: 'navigator_offline',
        };
    }

    try {
        await axios.get(PROBE_ENDPOINT, {
            timeout: 5000,
            headers: {'X-Requested-With': 'XMLHttpRequest'},
        });

        return {
            reachable: true,
            endpoint: PROBE_ENDPOINT,
            latencyMs: Math.round(performance.now() - startedAt),
        };
    }
    catch (error) {
        const isTimeout = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');

        return {
            reachable: false,
            endpoint: PROBE_ENDPOINT,
            latencyMs: Math.round(performance.now() - startedAt),
            reason: isTimeout ? 'probe_timeout' : 'probe_failed',
            statusCode: error?.response?.status || null,
            message: error?.message || null,
        };
    }
}

async function runBackgroundProbe() {
    if (probeInFlight || typeof document === 'undefined' || document.visibilityState === 'hidden') {
        return;
    }

    probeInFlight = true;

    try {
        const probe = await probeNetworkReachability();
        const reachable = probe.reachable;
        const previous = lastProbeReachable;

        lastProbeReachable = reachable;
        notifyProbeListeners(reachable);

        if (previous === null) {
            return;
        }

        if (reachable === previous) {
            return;
        }

        const event = reachable ? 'probe_online' : 'probe_offline';

        await logConnectivityChange(event, {
            source: 'background_probe',
            probe,
        });
    }
    finally {
        probeInFlight = false;
    }
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
        runBackgroundProbe();
    }
}

export function startBackgroundConnectivityMonitor(options = {}) {
    const intervalMs = options.intervalMs || DEFAULT_PROBE_INTERVAL_MS;

    stopBackgroundConnectivityMonitor();

    runBackgroundProbe();

    monitorIntervalId = window.setInterval(runBackgroundProbe, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
}

export function stopBackgroundConnectivityMonitor() {
    if (monitorIntervalId) {
        window.clearInterval(monitorIntervalId);
        monitorIntervalId = null;
    }

    document.removeEventListener('visibilitychange', onVisibilityChange);
    probeInFlight = false;
}

export async function sendConnectivityLog(event, deviceInfo = null, meta = {}) {
    const payload = {
        event,
        source: meta.source || 'browser_event',
        reportedAt: new Date().toISOString(),
        device: deviceInfo || await collectDeviceInfo(),
        probe: meta.probe || null,
        transport: meta.transport || 'http',
    };

    try {
        await axios.post('/offline-log', payload);
        return true;
    }
    catch (error) {
        enqueue(payload);
        return false;
    }
}

export async function flushQueuedConnectivityLogs() {
    const queue = readQueue();

    if (!queue.length) {
        return;
    }

    const remaining = [];

    for (const payload of queue) {
        try {
            await axios.post('/offline-log', {
                ...payload,
                flushedAt: new Date().toISOString(),
                wasQueued: true,
            });
        }
        catch (error) {
            remaining.push(payload);
        }
    }

    writeQueue(remaining);
}

export async function logConnectivityChange(event, options = {}) {
    const source = options.source || 'browser_event';
    const probe = options.probe || null;
    const deviceInfo = await collectDeviceInfo({
        logSource: source,
        connectivityProbe: probe,
    });

    if (event === 'online' || event === 'probe_online') {
        await flushQueuedConnectivityLogs();
    }

    const sent = await sendConnectivityLog(event, deviceInfo, {
        source,
        probe,
        transport: options.transport || 'http',
    });

    if (event === 'offline' && !sent && navigator.sendBeacon) {
        trySendBeaconLog({
            event,
            source,
            reportedAt: new Date().toISOString(),
            device: deviceInfo,
            probe,
            transport: 'beacon',
        });
    }
}

function trySendBeaconLog(payload) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!csrfToken || !navigator.sendBeacon) {
        return false;
    }

    const formData = new FormData();
    formData.append('_token', csrfToken);
    formData.append('event', payload.event);
    formData.append('source', payload.source || 'beacon');
    formData.append('reportedAt', payload.reportedAt);
    formData.append('transport', payload.transport || 'beacon');
    formData.append('device', JSON.stringify(payload.device || {}));

    if (payload.probe) {
        formData.append('probe', JSON.stringify(payload.probe));
    }

    if (payload.wasQueued) {
        formData.append('wasQueued', '1');
    }

    return navigator.sendBeacon('/offline-log', formData);
}
